// @view/panels/LoadingOverlay.panel.tsx

import React from "react";
import * as Option from "@utils/Option";
import EmptyView from "@view/EmptyView";
import OuroborosSpinnerComponent from "@view/OuroborosSpinner";
import * as styles from "@styles/LoadingOverlay.css";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";

export type LoadingOverlayPhase = "idle" | "loading" | "error";
export type SpinnerVariant = "sexy" | "fallback";

type LoadingOverlayLoadingProps = {
  spinnerVariant: SpinnerVariant;
};

type LoadingOverlayErrorProps = {
  message: string;
  info: OptionTs<string>;
  onDismiss: IOts<Ok>;
};

type LoadingOverlayPanelProps = {
  phase: LoadingOverlayPhase;
  loading: LoadingOverlayLoadingProps;
  error: LoadingOverlayErrorProps;
};

const LoadingOverlayPanel: React.FC<LoadingOverlayPanelProps> = ({
  phase,
  loading,
  error,
}) => {
  const emptyView = <EmptyView />;
  const spinnerByVariant: Record<SpinnerVariant, React.ReactElement> = {
    sexy: <OuroborosSpinnerComponent />,
    fallback: <div className={styles.spinnerFallback} />,
  };

  const loadingPanel = (
    <div className={styles.panel}>{spinnerByVariant[loading.spinnerVariant]}</div>
  );
  const errorPanel = (
    <div className={styles.panel}>
      <p className={styles.errorText}>
        {error.message.trim().length > 0
          ? error.message
          : "Something went wrong. Please try again."}
      </p>
      {Option.fold(error.info, () => emptyView, (value) => (
        <p className={styles.additionalInfoText}>{value}</p>
      ))}
      <button className={styles.dismissButton} onClick={() => error.onDismiss()}>
        Dismiss
      </button>
    </div>
  );

  const phaseView: Record<LoadingOverlayPhase, React.ReactElement> = {
    idle: emptyView,
    loading: <div className={styles.overlay}>{loadingPanel}</div>,
    error: <div className={styles.overlay}>{errorPanel}</div>,
  };

  return phaseView[phase];
};

export default LoadingOverlayPanel;
